import { shade } from 'polished';
import styled, { css, FlattenInterpolation, ThemeProps } from 'styled-components';
import { buttonResetStyleIncludingFocus } from '../../../../commonStyles/buttonReset';
import { calculateGridItemChildWidth } from '../../../../commonStyles/innerGridItemColumn';
import colors from '../../../../helpers/colors';
import fonts from '../../../../helpers/fonts';
import mediaQuery, { stylePerBreakpoint } from '../../../../helpers/mediaQuery';
import spacing, { spacingWithBorder } from '../../../../helpers/spacing';
import { Theme } from '../../../../theme';
import fluidGridColumns from '../../../../theme/fluidGridColumns';
import { PrimaryCta, SecondaryCta } from '../../../Cta';
import Icon from '../../../Icon';
import { GridAlignedText } from '../../commonStyles/commonHeaderStyles';
import { IsActiveProps } from '../../types';

export const baseSectionItemStyles = css`
  display: block;
  background: ${colors.neutral08};
  width: 100%;
  /* The spacing on the left and right causes the child to sit precisely on the full width of the grid
     for mobile and tablet portrait */
  ${stylePerBreakpoint(
    (breakpoint) => css`
      padding: ${spacing(2)} ${({ theme }) => theme.layout[breakpoint].gutterPx}px;
    `
  )}
`;

export const eventStyles = (
  color: ({ theme }: ThemeProps<Theme>) => string
): FlattenInterpolation<ThemeProps<Theme>> => css`
  position: relative;

  &:focus-visible,
  &:active {
    &::after {
      content: '';
      position: absolute;
      top: ${spacing(1)};
      left: ${spacing(1)};
      height: calc(100% - ${spacing(2)});
      width: calc(100% - ${spacing(2)});
      border-radius: 8px;
      box-shadow: 0 0 0 2px ${color};
    }
  }
`;

const DefaultNavSubmenuHeadingStyling = css`
  ${buttonResetStyleIncludingFocus}

  ${fonts.headingSmall}
& {
    font-weight: lighter;
  }

  ${baseSectionItemStyles}

  &:not(:first-of-type) {
    border-top: solid 1px ${colors.neutral06};
    padding-top: ${spacingWithBorder(2, 1)};
  }

  ${eventStyles(colors.core02)}
`;

export const SimplePrimaryNavSubmenuHeading = styled.button<IsActiveProps>`
  ${DefaultNavSubmenuHeadingStyling}
  ${({ isActive }) =>
    isActive &&
    css`
      & {
        color: ${colors.core02};
        font-weight: normal;
      }
    `}
`;

export const NestedPrimaryNavSubmenuHeading = styled.button<IsActiveProps>`
  ${DefaultNavSubmenuHeadingStyling}
  ${({ isActive }) =>
    isActive &&
    css`
      background-color: ${colors.neutral07};
      border-bottom: solid 1px ${colors.neutral06};
      padding-bottom: ${spacingWithBorder(2, 1)};
    `}
`;

export const SecondaryNavSubmenuHeading = styled.button<IsActiveProps>`
  ${DefaultNavSubmenuHeadingStyling}
  & {
    color: ${colors.neutral08};
  }

  transition: background-color 0.3s ease-out;
  background-color: ${colors.core02};
  ${({ isActive }) =>
    isActive &&
    css`
      background-color: ${({ theme }) => shade(0.3, theme.colors.core02)};
      & {
        font-weight: lighter;
      }
    `};

  ${eventStyles(colors.neutral08)}
`;

export const StyledGridAlignedText = styled(GridAlignedText)`
  > ${Icon} {
    padding-left: ${spacing(2)};
  }
`;

export const SimplePrimaryNavCtaSection = styled.div`
  ${baseSectionItemStyles}
  display: flex;
  flex-direction: column;
  margin: auto;
  align-items: flex-start;

  /* Define explicitly to avoid overriding side padding styles in baseSectionItemStyles */
  & {
    padding-top: ${spacing(4)};
    padding-bottom: ${spacing(4)};
  }
`;

export const NestedPrimaryNavCtaSection = styled(SimplePrimaryNavCtaSection)`
  border-top: solid 1px ${colors.neutral06};
  padding-top: ${spacingWithBorder(4, 1)};
`;

export const SecondaryNavCtaSection = styled(SimplePrimaryNavCtaSection)`
  background-color: ${colors.neutral06};
  border-top: solid 1px ${colors.neutral08};
  padding-top: ${spacingWithBorder(4, 1)};
`;

const ctaStyles = css`
  :not(:first-child) {
    margin-top: ${spacing(2)};
  }

  width: 100%;
  ${mediaQuery.tabletPortrait`
  width: auto;
  /* Force the element to side in a 6 column grid */
  margin: 0 calc((100% - ${calculateGridItemChildWidth(
    fluidGridColumns.tabletPortrait,
    6,
    'tabletPortrait'
  )}) / 2);
`}
`;

export const StyledPrimaryCta = styled(PrimaryCta)`
  ${ctaStyles}
`;

export const StyledSecondaryCta = styled(SecondaryCta)`
  ${ctaStyles}
`;
